// Multi-chain RPC query functions for wallet viewer
// Direct frontend queries to bypass IC HTTP outcall restrictions
// WITH AGGRESSIVE CACHING for instant results
import { ChainId, getChainRPCUrls, getChainMetadata } from './chainRegistry';
import { Connection, PublicKey } from '@solana/web3.js';
import { HttpAgent } from '@dfinity/agent';
import { Principal } from '@dfinity/principal';
import { fetchRavencoinNFTsDirectRPC, fetchWalletAssetBalances } from './ravencoinRpcClient';

// ==================== INSTANT CACHING SYSTEM ====================
// Cache wallet data in browser for instant repeat queries
// Cache expires after 2 minutes to stay reasonably fresh

interface CachedWalletData {
  tokens: MultiChainToken[];
  nfts: MultiChainNFT[];
  timestamp: number;
  chains: ChainId[];
}

const CACHE_DURATION = 120000; // 2 minutes
const walletCache = new Map<string, CachedWalletData>();

function getCacheKey(address: string, chains: ChainId[] | readonly ChainId[]): string {
  const chainArray = Array.isArray(chains) ? chains : [...chains];
  return `${address}:${chainArray.sort().join(',')}`;
}

function getCachedData(address: string, chains: ChainId[]): CachedWalletData | null {
  const key = getCacheKey(address, chains);
  const cached = walletCache.get(key);
  
  if (!cached) return null;
  
  // Check if cache is still fresh
  if (Date.now() - cached.timestamp > CACHE_DURATION) {
    walletCache.delete(key);
    return null;
  }
  
  return cached;
}

function setCachedData(address: string, chains: ChainId[], tokens: MultiChainToken[], nfts: MultiChainNFT[]): void {
  const key = getCacheKey(address, chains);
  walletCache.set(key, {
    tokens,
    nfts,
    timestamp: Date.now(),
    chains,
  });
  
  // Limit cache size to prevent memory issues (max 50 wallets)
  if (walletCache.size > 50) {
    const firstKey = walletCache.keys().next().value;
    walletCache.delete(firstKey);
  }
}

export interface MultiChainToken {
  chain: ChainId;
  contractAddress?: string;
  symbol: string;
  name: string;
  balance: number;
  decimals: number;
  logoUrl?: string;
}

export interface MultiChainNFT {
  chain: ChainId;
  contractAddress: string;
  tokenId: string;
  name?: string;
  description?: string;
  imageUrl?: string;
  collectionName?: string;
}

const RPC_TIMEOUT = 15000; // 15 seconds (increased for reliability)

// ==================== STANDARDIZED JSON-RPC 2.0 BATCH FUNCTION ====================
// Generic batch RPC function for ANY JSON-RPC 2.0 compatible chain (Ethereum, BSC, Polygon, etc.)
// Reduces HTTP requests from N to 1 per batch (industry standard practice)
interface BatchRPCRequest {
  method: string;
  params: unknown[];
  id: number;
}

interface BatchRPCResponse {
  id: number;
  result?: unknown;
  error?: { code: number; message: string; data?: unknown };
}

async function callJsonRpcBatch(
  primaryUrl: string,
  fallbackUrl: string | null,
  requests: BatchRPCRequest[],
  batchSize: number = 100
): Promise<BatchRPCResponse[]> {
  const allResults: BatchRPCResponse[] = [];
  
  // Process in batches to avoid overwhelming the RPC endpoint
  for (let i = 0; i < requests.length; i += batchSize) {
    const batch = requests.slice(i, i + batchSize);
    const requestBody = batch.map(req => ({
      jsonrpc: '2.0' as const,
      id: req.id,
      method: req.method,
      params: req.params,
    }));
    
    const tryRPC = async (url: string): Promise<BatchRPCResponse[]> => {
      const response = await Promise.race([
        fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          body: JSON.stringify(requestBody),
        }),
        new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('Batch RPC timeout')), RPC_TIMEOUT)
        ),
      ]);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      
      // Batch responses come back as array
      if (Array.isArray(data)) {
        return data;
      }
      
      // Single response (shouldn't happen but handle it)
      return [data];
    };

    // Try primary first
    let batchResults: BatchRPCResponse[] | null = null;
    if (primaryUrl) {
      try {
        batchResults = await tryRPC(primaryUrl);
      } catch (error) {
        console.warn(`Primary RPC batch failed, trying fallback:`, error);
      }
    }

    // Try fallback if primary failed
    if (!batchResults && fallbackUrl) {
      try {
        batchResults = await tryRPC(fallbackUrl);
      } catch (error) {
        console.error(`Both RPC endpoints failed for batch:`, error);
        // Create error responses for failed batch
        batchResults = batch.map(req => ({
          id: req.id,
          error: { code: -1, message: 'RPC request failed' },
        }));
      }
    }

    if (batchResults) {
      allResults.push(...batchResults);
    }
  }

  return allResults;
}

// IPFS gateway fallbacks (prioritize faster gateways)
// Note: QuickNode doesn't serve IPFS content, so we use public gateways
// Order: Fastest/most reliable first
const IPFS_GATEWAYS = [
  'https://gateway.pinata.cloud/ipfs/', // Pinata - usually fastest
  'https://nftstorage.link/ipfs/', // NFT.Storage - fast CDN
  'https://ipfs.filebase.io/ipfs/', // Filebase - reliable
  'https://dweb.link/ipfs/', // Protocol Labs - decent
  'https://ipfs.io/ipfs/', // Public gateway - slowest, use as last resort
];

// Common ERC-20 token contracts on Ethereum Mainnet
const COMMON_ERC20_TOKENS: Array<[string, string, number]> = [
  ['USDT', '0xdAC17F958D2ee523a2206206994597C13D831ec7', 6],  // Tether
  ['USDC', '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48', 6],  // USD Coin
  ['DAI', '0x6B175474E89094C44Da98b954EedeAC495271d0F', 18], // Dai Stablecoin
  ['WBTC', '0x2260FAC5E5542a773Aa44fBCfeDf7C193bc2C599', 8], // Wrapped Bitcoin
  ['UNI', '0x1f9840a85d5aF5bf1D1762F925BDADdC4201F984', 18],  // Uniswap
  ['LINK', '0x514910771AF9Ca656af840dff83E8264EcF986CA', 18], // Chainlink
];

// Common ERC-721 NFT contracts on Ethereum Mainnet
const COMMON_NFT_CONTRACTS: Array<[string, string]> = [
  ['CryptoPunks', '0xb47e3cd837dDF8e4c57F05d70Ab865de6e193BBB'],
  ['Bored Ape Yacht Club', '0xBC4CA0EdA7647A8aB7C2061c2E118A18a936f13D'],
  ['Azuki', '0xED5AF388653567Af2F388E6224dC7C4b3241C544'],
];

// RPC URL functions are now imported from chainRegistry.ts

// Convert hex string to number (BigInt-safe)
function hexToNumber(hex: string): number {
  if (!hex || !hex.startsWith('0x')) {
    return 0;
  }
  // Remove 0x prefix
  const hexClean = hex.slice(2);
  // Use BigInt for large numbers, then convert to Number
  try {
    const bigIntValue = BigInt('0x' + hexClean);
    // Convert to number (may lose precision for very large numbers, but balances should be fine)
    return Number(bigIntValue);
  } catch {
    return 0;
  }
}

// Query native token balance (ETH, BNB, MATIC, etc.) with fallback
// Handles both EVM chains (eth_getBalance) and UTXO chains (Ravencoin)
export async function fetchNativeBalance(chain: ChainId, address: string): Promise<number> {
  const chainMeta = getChainMetadata(chain);
  if (!chainMeta) {
    console.error(`Unknown chain: ${chain}`);
    return 0;
  }

  // Handle UTXO chains (Ravencoin) differently
  if (chainMeta.type === 'UTXO' && chain === 'ravencoin') {
    return await fetchRavencoinNativeBalance(address);
  }

  // Handle Solana differently
  if (chain === 'solana') {
    return await fetchSolanaNativeBalance(address);
  }

  // Handle ICP differently
  if (chain === 'icp') {
    return await fetchICPNativeBalance(address);
  }

  // EVM chains use eth_getBalance
  const [primaryUrl, fallbackUrl] = getChainRPCUrls(chain);
  const rpcUrl = primaryUrl || fallbackUrl;
  
  if (!rpcUrl) {
    console.error(`No RPC URL configured for chain: ${chain}`);
    return 0;
  }

  if (import.meta.env.DEV) {
    console.log(`🔍 Fetching ${chain} balance for ${address} using ${primaryUrl ? 'QuickNode' : 'public RPC'}`);
  }

  // Try primary first, then fallback on error
  const tryRPC = async (url: string): Promise<number> => {
    const response = await Promise.race([
      fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          jsonrpc: '2.0',
          method: 'eth_getBalance',
          params: [address, 'latest'],
          id: 1,
        }),
      }),
      new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('RPC timeout')), RPC_TIMEOUT)
      ),
    ]);

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const data = await response.json();
    
    if (data.error) {
      throw new Error(data.error.message || 'RPC error');
    }

    if (data.result) {
      const balanceWei = hexToNumber(data.result);
      // Convert from Wei to ETH (divide by 10^18)
      return balanceWei / 1e18;
    }

    throw new Error('No result in response');
  };

  // Try primary first
  if (primaryUrl) {
    try {
      const balance = await tryRPC(primaryUrl);
      if (import.meta.env.DEV) {
        console.log(`✅ ${chain} balance (QuickNode):`, balance);
      }
      return balance;
    } catch (error: any) {
      console.warn(`⚠️ Primary RPC (QuickNode) failed for ${chain}, trying fallback:`, error?.message || error);
    }
  }

  // Try fallback
  if (fallbackUrl) {
    try {
      const balance = await tryRPC(fallbackUrl);
      if (import.meta.env.DEV) {
        console.log(`✅ ${chain} balance (public RPC):`, balance);
      }
      return balance;
    } catch (error: any) {
      console.error(`❌ Fallback RPC also failed for ${chain}:`, error?.message || error);
    }
  }

  return 0;
}

// Ravencoin-specific RPC functions
// Fetch Ravencoin native balance (RVN) with primary/fallback support
// Uses proper RPC configuration from walletConnectors for consistency
async function fetchRavencoinNativeBalance(address: string): Promise<number> {
  try {
    // Use the proper fetchRavencoinBalance from walletConnectors for consistency
    // This ensures we use the same RPC configuration and error handling
    const { fetchRavencoinBalance } = await import('./walletConnectors');
    return await fetchRavencoinBalance(address);
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error('Failed to fetch Ravencoin balance:', error);
    }
    return 0;
  }
}

// Fetch Ravencoin assets (tokens) for an address with primary/fallback support
async function fetchRavencoinAssets(address: string): Promise<MultiChainToken[]> {
  try {
    // Use fetchWalletAssetBalances from ravencoinRpcClient for consistency
    const balances = await fetchWalletAssetBalances(address);
    
    if (!balances || Object.keys(balances).length === 0) {
      return [];
    }
    
    // Collect all asset names with balances
    const assetsWithBalances = Object.entries(balances)
      .filter(([_, quantity]) => typeof quantity === 'number' && quantity > 0)
      .map(([assetName, quantity]) => ({ assetName, quantity }));
    
    // Fetch metadata using JSON-RPC 2.0 batch requests (ONE HTTP call instead of 210!)
    const { fetchAssetMetadataBatch } = await import('./ravencoinRpcClient');
    const assetNames = assetsWithBalances.map(a => a.assetName);
    const metadataMap = await fetchAssetMetadataBatch(assetNames, 100); // 100 requests per batch (JSON-RPC standard)
    
    const results: MultiChainToken[] = [];
    
    // Convert balances to MultiChainToken format - EXCLUDE NFTs
    for (const { assetName, quantity } of assetsWithBalances) {
      const metadata = metadataMap.get(assetName);
      const hasIPFS = metadata?.ipfs_hash && typeof metadata.ipfs_hash === 'string' && metadata.ipfs_hash.trim();
      
      if (!hasIPFS) {
        const balance = (quantity as number) / 100000000; // Ravencoin uses 8 decimals
        results.push({
          chain: 'ravencoin',
          contractAddress: assetName,
          symbol: assetName,
          name: assetName,
          balance,
          decimals: 8,
          logoUrl: undefined,
        });
      }
    }
    
    return results;
  } catch (error: any) {
    if (import.meta.env.DEV) {
      console.error('Failed to fetch Ravencoin assets:', error?.message || error);
    }
    return [];
  }
}



// Fetch Ravencoin NFTs (assets with quantity 1)
async function fetchRavencoinNFTs(address: string): Promise<MultiChainNFT[]> {
  try {
    // Use the proper Ravencoin RPC client with full metadata support
    const rvnNFTs = await fetchRavencoinNFTsDirectRPC(address);
    
    // Convert RavencoinNFTResult to MultiChainNFT format
    return rvnNFTs.map((nft) => {
      // Extract image URL from the RavencoinNFTResult format
      const imageUrl = Array.isArray(nft.imageUrl) 
        ? (nft.imageUrl.length > 0 ? nft.imageUrl[0] : undefined)
        : nft.imageUrl || undefined;
      
      // Extract description
      const description = Array.isArray(nft.description)
        ? (nft.description.length > 0 ? nft.description[0] : undefined)
        : nft.description || undefined;
      
      return {
        chain: 'ravencoin' as ChainId,
        contractAddress: nft.assetName,
        tokenId: '1',
        name: nft.assetName,
        description: description || `Ravencoin Asset: ${nft.assetName}`,
        imageUrl: imageUrl,
        collectionName: nft.assetName,
      };
    });
  } catch (error: any) {
    if (import.meta.env.DEV) {
      console.error('Failed to fetch Ravencoin NFTs:', error?.message || error);
    }
    return [];
  }
}

// Query a single ERC-20 token balance with fallback
// Batch fetch ERC-20 token balances using JSON-RPC 2.0 batch requests (ONE HTTP call instead of N!)
async function fetchERC20TokenBalancesBatch(
  primaryUrl: string,
  fallbackUrl: string | null,
  tokens: Array<{ contractAddress: string; decimals: number }>,
  walletAddress: string
): Promise<Map<string, number>> {
  const results = new Map<string, number>();
  
  if (tokens.length === 0) {
    return results;
  }

  // Prepare batch requests
  const addressClean = walletAddress.startsWith('0x') ? walletAddress.slice(2) : walletAddress;
  const paddedAddress = addressClean.padStart(64, '0');
  const balanceOfData = '0x70a08231' + paddedAddress; // ERC-20 balanceOf(address) selector

  const requests: BatchRPCRequest[] = tokens.map((token, index) => ({
    method: 'eth_call',
    params: [
      {
        to: token.contractAddress,
        data: balanceOfData,
      },
      'latest',
    ],
    id: index,
  }));

  // Execute batch request
  const responses = await callJsonRpcBatch(primaryUrl, fallbackUrl, requests);

  // Process responses
  responses.forEach((response, index) => {
    const token = tokens[index];
    if (!token) return;

    if (response.error || !response.result || response.result === '0x0') {
      results.set(token.contractAddress, 0);
    } else {
      const balanceWei = hexToNumber(response.result as string);
      const divisor = 10 ** token.decimals;
      results.set(token.contractAddress, balanceWei / divisor);
    }
  });

  return results;
}

// Legacy single token function (kept for compatibility, but should use batch version)
async function fetchERC20TokenBalance(
  primaryUrl: string,
  fallbackUrl: string,
  contractAddress: string,
  walletAddress: string,
  decimals: number
): Promise<number> {
  const results = await fetchERC20TokenBalancesBatch(
    primaryUrl,
    fallbackUrl,
    [{ contractAddress, decimals }],
    walletAddress
  );
  return results.get(contractAddress) || 0;
}

// ==================== SOLANA SUPPORT ====================

// Fetch Solana native SOL balance
// Tries to use wallet connection first (if available), then falls back to RPC
async function fetchSolanaNativeBalance(address: string): Promise<number> {
  try {
    // First, try to use the wallet's connection if available (for embedded wallets)
    // This is more reliable and uses the wallet's own RPC endpoint
    const { fetchSolanaBalance } = await import('./solanaWalletConnector');
    try {
      const balance = await fetchSolanaBalance(address);
      if (balance > 0 || address) {
        // If we got a result (even 0) or have an address, use it
        return balance;
      }
    } catch (walletError) {
      // Wallet method failed, fall through to RPC
      if (import.meta.env.DEV) {
        console.warn('Wallet-based Solana balance fetch failed, using RPC fallback:', walletError);
      }
    }

    // Fallback: Use RPC endpoint from config
    const [primaryUrl] = getChainRPCUrls('solana');
    if (!primaryUrl) {
      console.error('No Solana RPC URL configured');
      return 0;
    }

    const connection = new Connection(primaryUrl, 'confirmed');
    const publicKey = new PublicKey(address);
    const balance = await connection.getBalance(publicKey);
    
    // Solana uses 9 decimals (lamports)
    return balance / 1e9;
  } catch (error) {
    console.error('Error fetching Solana balance:', error);
    return 0;
  }
}

// Fetch Solana SPL tokens (similar to ERC-20)
async function fetchSolanaTokens(address: string): Promise<MultiChainToken[]> {
  try {
    const [primaryUrl] = getChainRPCUrls('solana');
    if (!primaryUrl) {
      console.error('No Solana RPC URL configured');
      return [];
    }

    const connection = new Connection(primaryUrl, 'confirmed');
    const publicKey = new PublicKey(address);
    
    // Get all SPL token accounts owned by this address
    const tokenAccounts = await connection.getParsedTokenAccountsByOwner(publicKey, {
      programId: new PublicKey('TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA'), // SPL Token Program
    });

    const tokens: MultiChainToken[] = [];

    for (const { account } of tokenAccounts.value) {
      const parsedInfo = account.data.parsed.info;
      const mint = parsedInfo.mint;
      const balance = parsedInfo.tokenAmount.uiAmount;
      const decimals = parsedInfo.tokenAmount.decimals;

      // Only include tokens with balance > 0
      if (balance > 0) {
        tokens.push({
          chain: 'solana',
          contractAddress: mint,
          symbol: mint.slice(0, 6) + '...', // Short address as symbol (can be enhanced with metadata)
          name: 'SPL Token',
          balance,
          decimals,
          logoUrl: undefined,
        });
      }
    }

    return tokens;
  } catch (error) {
    console.error('Error fetching Solana tokens:', error);
    return [];
  }
}

// Fetch Solana NFTs (Metaplex standard)
async function fetchSolanaNFTs(address: string): Promise<MultiChainNFT[]> {
  try {
    const [primaryUrl] = getChainRPCUrls('solana');
    if (!primaryUrl) {
      console.error('No Solana RPC URL configured');
      return [];
    }

    const connection = new Connection(primaryUrl, 'confirmed');
    const publicKey = new PublicKey(address);
    
    // Get all token accounts (NFTs are SPL tokens with amount = 1 and decimals = 0)
    const tokenAccounts = await connection.getParsedTokenAccountsByOwner(publicKey, {
      programId: new PublicKey('TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA'),
    });

    const nfts: MultiChainNFT[] = [];

    for (const { account } of tokenAccounts.value) {
      const parsedInfo = account.data.parsed.info;
      const mint = parsedInfo.mint;
      const amount = parsedInfo.tokenAmount.uiAmount;
      const decimals = parsedInfo.tokenAmount.decimals;

      // NFTs typically have amount = 1 and decimals = 0
      if (amount === 1 && decimals === 0) {
        nfts.push({
          chain: 'solana',
          contractAddress: mint,
          tokenId: mint, // For Solana, mint address IS the unique ID
          name: `Solana NFT`,
          description: undefined,
          imageUrl: undefined, // Would need metadata fetch from arweave/IPFS
          collectionName: undefined,
        });
      }
    }

    return nfts;
  } catch (error) {
    console.error('Error fetching Solana NFTs:', error);
    return [];
  }
}

// ==================== ICP (INTERNET COMPUTER) SUPPORT ====================

// Fetch ICP native balance
async function fetchICPNativeBalance(principal: string): Promise<number> {
  try {
    const [primaryUrl] = getChainRPCUrls('icp');
    if (!primaryUrl) {
      console.error('No ICP RPC URL configured');
      return 0;
    }

    // ICP uses principals, not traditional addresses
    const principalObj = Principal.fromText(principal);
    const agent = new HttpAgent({ host: primaryUrl });
    
    // For production, we'd query the ICP ledger canister for balance
    // For now, return 0 as a placeholder (requires canister interface definition)
    // This would need the ICP ledger canister ID and proper Candid interface
    
    return 0; // Placeholder - would query ICP ledger canister
  } catch (error) {
    console.error('Error fetching ICP balance:', error);
    return 0;
  }
}

// Fetch ICP tokens (ckBTC, ckETH, etc.)
async function fetchICPTokens(principal: string): Promise<MultiChainToken[]> {
  try {
    // ICP chain-key tokens (ckBTC, ckETH, ckUSDT, ckUSDC, ckDOGE)
    // These are managed by specific canisters
    // Would need to query each canister for balance
    
    // Placeholder - would query ICP token canisters
    return [];
  } catch (error) {
    console.error('Error fetching ICP tokens:', error);
    return [];
  }
}

// Fetch ICP NFTs
async function fetchICPNFTs(principal: string): Promise<MultiChainNFT[]> {
  try {
    // ICP NFTs are canister-based
    // Popular standards: DIP-721, EXT, ICRC-7
    // Would need to query NFT canisters (e.g., Entrepot, Yuku, DSCVR)
    
    // Placeholder - would query ICP NFT canisters
    return [];
  } catch (error) {
    console.error('Error fetching ICP NFTs:', error);
    return [];
  }
}

// ==================== EVM TOKEN SUPPORT ====================

// Query ERC-20 token balances for an address (or Ravencoin assets)
export async function fetchERC20Tokens(
  chain: ChainId,
  address: string
): Promise<MultiChainToken[]> {
  const chainMeta = getChainMetadata(chain);
  // Only chains that support tokens
  if (!chainMeta || !chainMeta.supportsTokens) {
    return [];
  }

  // Handle Ravencoin assets differently (UTXO chain)
  if (chain === 'ravencoin') {
    return await fetchRavencoinAssets(address);
  }

  // Handle Solana SPL tokens differently
  if (chain === 'solana') {
    return await fetchSolanaTokens(address);
  }

  // Handle ICP tokens differently
  if (chain === 'icp') {
    return await fetchICPTokens(address);
  }

  const [primaryUrl, fallbackUrl] = getChainRPCUrls(chain);
  
  if (!primaryUrl && !fallbackUrl) {
    return [];
  }

  const results: MultiChainToken[] = [];

  // Prepare tokens for batch request
  const tokens = COMMON_ERC20_TOKENS.map(([symbol, contractAddress, decimals]) => ({
    symbol,
    contractAddress,
    decimals,
  }));

  // Fetch all balances in ONE HTTP request using JSON-RPC 2.0 batch
  const balanceMap = await fetchERC20TokenBalancesBatch(
    primaryUrl || '',
    fallbackUrl || null,
    tokens.map(t => ({ contractAddress: t.contractAddress, decimals: t.decimals })),
    address
  );

  // Build results from batch response
  tokens.forEach(({ symbol, contractAddress, decimals }) => {
    const balance = balanceMap.get(contractAddress) || 0;
    if (balance > 0) {
      results.push({
        chain,
        contractAddress,
        symbol,
        name: symbol,
        balance,
        decimals,
        logoUrl: undefined,
      });
    }
  });
  
  return results;
}

// Batch fetch tokenOfOwnerByIndex for NFT enumeration (ONE HTTP call instead of N!)
async function fetchTokenOfOwnerByIndexBatch(
  primaryUrl: string,
  fallbackUrl: string | null,
  contractAddress: string,
  walletAddress: string,
  indices: number[]
): Promise<Map<number, string>> {
  const results = new Map<number, string>();
  
  if (indices.length === 0) {
    return results;
  }

  // Prepare batch requests
  const addressClean = walletAddress.startsWith('0x') ? walletAddress.slice(2) : walletAddress;
  const paddedAddress = addressClean.padStart(64, '0');

  const requests: BatchRPCRequest[] = indices.map((index, idx) => {
    const paddedIndex = index.toString(16).padStart(64, '0');
    const data = '0x2f745c59' + paddedAddress + paddedIndex; // ERC-721 tokenOfOwnerByIndex selector
    return {
      method: 'eth_call',
      params: [
        {
          to: contractAddress,
          data: data,
        },
        'latest',
      ],
      id: idx,
    };
  });

  // Execute batch request
  const responses = await callJsonRpcBatch(primaryUrl, fallbackUrl, requests);

  // Process responses
  responses.forEach((response, idx) => {
    const index = indices[idx];
    if (index === undefined) return;

    if (response.error || !response.result || response.result === '0x0') {
      results.set(index, '');
    } else {
      const tokenIdHex = response.result as string;
      const tokenId = hexToNumber(tokenIdHex);
      results.set(index, tokenId.toString());
    }
  });

  return results;
}

// Legacy single index function (kept for compatibility)
async function fetchTokenOfOwnerByIndex(
  primaryUrl: string,
  fallbackUrl: string,
  contractAddress: string,
  walletAddress: string,
  index: number
): Promise<string> {
  const results = await fetchTokenOfOwnerByIndexBatch(
    primaryUrl,
    fallbackUrl,
    contractAddress,
    walletAddress,
    [index]
  );
  return results.get(index) || '';
}

// Batch fetch tokenURI for multiple NFTs (ONE HTTP call instead of N!)
async function fetchTokenURIBatch(
  primaryUrl: string,
  fallbackUrl: string | null,
  contractAddress: string,
  tokenIds: string[]
): Promise<Map<string, string>> {
  const results = new Map<string, string>();
  
  if (tokenIds.length === 0) {
    return results;
  }

  // Prepare batch requests
  const requests: BatchRPCRequest[] = tokenIds.map((tokenId, idx) => {
    const tokenIdNum = parseInt(tokenId, 10);
    if (isNaN(tokenIdNum)) {
      return { method: 'eth_call', params: [], id: idx }; // Will error, but maintain index mapping
    }
    const tokenIdHex = tokenIdNum.toString(16).padStart(64, '0');
    const data = '0xc87b56dd' + tokenIdHex; // tokenURI(uint256) selector
    return {
      method: 'eth_call',
      params: [
        {
          to: contractAddress,
          data: data,
        },
        'latest',
      ],
      id: idx,
    };
  });

  // Execute batch request
  const responses = await callJsonRpcBatch(primaryUrl, fallbackUrl, requests);

  // Process responses
  responses.forEach((response, idx) => {
    const tokenId = tokenIds[idx];
    if (!tokenId) return;

    if (response.error || !response.result || response.result === '0x0') {
      results.set(tokenId, '');
    } else {
      // Decode hex-encoded string (bytes32 ABI encoding)
      const hex = (response.result as string).slice(2); // Remove 0x
      
      if (hex.length < 128) {
        results.set(tokenId, '');
        return;
      }

      // First 32 bytes = offset, next 32 bytes = length
      const lengthHex = hex.slice(64, 128);
      const length = parseInt(lengthHex, 16);
      
      if (length === 0 || hex.length < 128 + length * 2) {
        results.set(tokenId, '');
        return;
      }

      // Extract string bytes
      const stringHex = hex.slice(128, 128 + length * 2);
      let uri = '';
      for (let i = 0; i < stringHex.length; i += 2) {
        const charCode = parseInt(stringHex.slice(i, i + 2), 16);
        if (charCode === 0) break; // Null terminator
        uri += String.fromCharCode(charCode);
      }

      // Handle IPFS URLs
      if (uri.startsWith('ipfs://')) {
        uri = uri.replace('ipfs://', `${IPFS_GATEWAYS[0]}`);
      }

      results.set(tokenId, uri);
    }
  });

  return results;
}

// Query balanceOf to get NFT count with fallback
async function fetchNFTBalance(
  primaryUrl: string,
  fallbackUrl: string,
  contractAddress: string,
  walletAddress: string
): Promise<number> {
  const rpcUrl = primaryUrl || fallbackUrl;
  // ERC-721 balanceOf(address) function selector: 0x70a08231
  const addressClean = walletAddress.startsWith('0x') ? walletAddress.slice(2) : walletAddress;
  const paddedAddress = addressClean.padStart(64, '0');
  const data = '0x70a08231' + paddedAddress;

  const tryRPC = async (url: string): Promise<number> => {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify({
        jsonrpc: '2.0',
        method: 'eth_call',
        params: [
          {
            to: contractAddress,
            data: data,
          },
          'latest',
        ],
        id: 1,
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const result = await response.json();
    
    if (result.error || !result.result || result.result === '0x0') {
      return 0;
    }

    const balance = hexToNumber(result.result);
    return Math.abs(balance);
  };

  // Try primary first
  if (primaryUrl) {
    try {
      return await tryRPC(primaryUrl);
    } catch (error) {
      console.warn(`Primary RPC failed for NFT balance, trying fallback:`, error);
    }
  }

  // Try fallback
  if (fallbackUrl) {
    try {
      return await tryRPC(fallbackUrl);
    } catch (error) {
      console.error(`Fallback RPC also failed for NFT balance:`, error);
    }
  }

  return 0;
}

// Legacy single token URI function (wrapper around batch for compatibility)
async function fetchTokenURI(
  primaryUrl: string,
  fallbackUrl: string,
  contractAddress: string,
  tokenId: string
): Promise<string> {
  const results = await fetchTokenURIBatch(
    primaryUrl,
    fallbackUrl || null,
    contractAddress,
    [tokenId]
  );
  return results.get(tokenId) || '';
}


// Fetch NFT metadata from URI with IPFS gateway fallbacks
async function fetchNFTMetadata(uri: string): Promise<{ imageUrl?: string; name?: string; description?: string }> {
  if (!uri) {
    return {};
  }

  // Handle IPFS URLs with multiple gateway fallbacks
  let fetchUrl = uri;
  if (uri.startsWith('ipfs://')) {
    const ipfsHash = uri.slice(7);
    
    // Try each IPFS gateway until one works
    for (const gateway of IPFS_GATEWAYS) {
      try {
        fetchUrl = `${gateway}${ipfsHash}`;
        const response = await fetch(fetchUrl, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
          },
          signal: AbortSignal.timeout(3000), // 3 second timeout per gateway (faster fallback)
        });

        if (response.ok) {
          const metadata = await response.json();
          
          // Also handle IPFS URLs in image field
          let imageUrl = metadata.image || metadata.image_url || undefined;
          if (imageUrl) {
            // Handle ipfs:// protocol
            if (imageUrl.startsWith('ipfs://')) {
              const imageHash = imageUrl.slice(7).split('/')[0]; // Get hash, ignore any path after
              imageUrl = `${IPFS_GATEWAYS[0]}${imageHash}`;
              if (import.meta.env.DEV) {
                console.log(`🖼️ Converted IPFS image URL: ${imageUrl}`);
              }
            }
            // If it's already a gateway URL, keep it as-is
            // If it's a relative path or other format, log it for debugging
            else if (!imageUrl.startsWith('http://') && !imageUrl.startsWith('https://')) {
              if (import.meta.env.DEV) {
                console.warn(`⚠️ Unexpected image URL format: ${imageUrl}`);
              }
            }
          }
          
          return {
            imageUrl,
            name: metadata.name || undefined,
            description: metadata.description || undefined,
          };
        }
      } catch (error) {
        // Try next gateway
        continue;
      }
    }
    
    // All gateways failed
    console.warn(`All IPFS gateways failed for ${ipfsHash}`);
    return {};
  }

  // Handle regular HTTP/HTTPS URLs
  try {
    const response = await fetch(fetchUrl, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
      },
      signal: AbortSignal.timeout(3000), // 3 second timeout (faster fallback)
    });

    if (!response.ok) {
      return {};
    }

    const metadata = await response.json();
    
    // Handle IPFS URLs in image field
    let imageUrl = metadata.image || metadata.image_url || undefined;
    if (imageUrl) {
      // Handle ipfs:// protocol
      if (imageUrl.startsWith('ipfs://')) {
        const imageHash = imageUrl.slice(7).split('/')[0]; // Get hash, ignore any path after
        imageUrl = `${IPFS_GATEWAYS[0]}${imageHash}`;
        if (import.meta.env.DEV) {
          console.log(`🖼️ Converted IPFS image URL: ${imageUrl}`);
        }
      }
      // If it's already a gateway URL, keep it as-is
      // If it's a relative path or other format, log it for debugging
      else if (!imageUrl.startsWith('http://') && !imageUrl.startsWith('https://')) {
        if (import.meta.env.DEV) {
          console.warn(`⚠️ Unexpected image URL format: ${imageUrl}`);
        }
      }
    }
    
    return {
      imageUrl,
      name: metadata.name || undefined,
      description: metadata.description || undefined,
    };
  } catch (error) {
    console.error(`Failed to fetch NFT metadata from ${uri}:`, error);
    return {};
  }
}

// Query NFTs from a specific contract
async function fetchNFTsFromContract(
  chain: ChainId,
  primaryUrl: string,
  fallbackUrl: string,
  contractAddress: string,
  walletAddress: string,
  collectionName: string
): Promise<MultiChainNFT[]> {
  const results: MultiChainNFT[] = [];

  try {
    // Get NFT count
    const nftCount = await fetchNFTBalance(primaryUrl, fallbackUrl, contractAddress, walletAddress);
    
    if (nftCount === 0) {
      return [];
    }

    // Limit to 5 NFTs per collection to avoid too many RPC calls (reduced from 10)
    const maxNFTs = Math.min(nftCount, 5);
    
    // Batch fetch all token IDs in ONE HTTP request (JSON-RPC 2.0 batch)
    const indices = Array.from({ length: maxNFTs }, (_, i) => i);
    const tokenIdMap = await fetchTokenOfOwnerByIndexBatch(
      primaryUrl,
      fallbackUrl || null,
      contractAddress,
      walletAddress,
      indices
    );
    
    // Extract valid token IDs
    const tokenIds: string[] = [];
    indices.forEach(index => {
      const tokenId = tokenIdMap.get(index);
      if (tokenId) {
        tokenIds.push(tokenId);
      }
    });
    
    if (tokenIds.length === 0) {
      return [];
    }
    
    // Batch fetch all token URIs in ONE HTTP request (JSON-RPC 2.0 batch)
    const tokenURIMap = await fetchTokenURIBatch(
      primaryUrl,
      fallbackUrl || null,
      contractAddress,
      tokenIds
    );
    
    // Fetch metadata (non-blocking - show NFT even if image fails)
    const nftPromises = tokenIds.map(async (tokenId) => {
      try {
        const tokenURI = tokenURIMap.get(tokenId) || '';
        // Fetch metadata but don't block on failures - show NFT even if image fails
        const metadata = tokenURI ? await fetchNFTMetadata(tokenURI).catch(() => ({})) : {};
        
        const nft = {
          chain,
          contractAddress,
          tokenId,
          name: metadata.name || `${collectionName} #${tokenId}`,
          description: metadata.description,
          imageUrl: metadata.imageUrl,
          collectionName,
        };
        
        if (import.meta.env.DEV) {
          console.log(`📦 NFT created: ${nft.name}, imageUrl: ${nft.imageUrl || 'none'}`);
        }
        
        return nft;
      } catch (error) {
        console.error(`Error fetching metadata for NFT ${tokenId} from ${collectionName}:`, error);
        // Return NFT without metadata rather than failing completely
        return {
          chain,
          contractAddress,
          tokenId,
          name: `${collectionName} #${tokenId}`,
          description: undefined,
          imageUrl: undefined,
          collectionName,
        };
      }
    });
    
    const nftResults = await Promise.allSettled(nftPromises);
    nftResults.forEach((result) => {
      if (result.status === 'fulfilled' && result.value) {
        results.push(result.value);
      }
    });
  } catch (error) {
    console.error(`Failed to fetch NFTs from ${contractAddress}:`, error);
  }

  return results;
}

// Query NFTs for an address (ERC-721 or Ravencoin assets)
export async function fetchNFTs(
  chain: ChainId,
  address: string
): Promise<MultiChainNFT[]> {
  const chainMeta = getChainMetadata(chain);
  // Only chains that support NFTs
  if (!chainMeta || !chainMeta.supportsNFTs) {
    return [];
  }

  // Handle Ravencoin NFTs differently (UTXO chain)
  if (chain === 'ravencoin') {
    return await fetchRavencoinNFTs(address);
  }

  // Handle Solana NFTs differently
  if (chain === 'solana') {
    return await fetchSolanaNFTs(address);
  }

  // Handle ICP NFTs differently
  if (chain === 'icp') {
    return await fetchICPNFTs(address);
  }

  const [primaryUrl, fallbackUrl] = getChainRPCUrls(chain);
  
  if (!primaryUrl && !fallbackUrl) {
    console.error('No RPC URL configured for NFTs');
    return [];
  }

  if (import.meta.env.DEV) {
    console.log(`🔍 Fetching NFTs for ${address}`);
  }

  const results: MultiChainNFT[] = [];

  try {
    // Query all common NFT collections in parallel
    const nftQueries = COMMON_NFT_CONTRACTS.map(async ([collectionName, contractAddress]) => {
      try {
        return await fetchNFTsFromContract(chain, primaryUrl, fallbackUrl, contractAddress, address, collectionName);
      } catch (error) {
        console.error(`Error fetching NFTs from ${collectionName}:`, error);
        return [];
      }
    });

    const nftResults = await Promise.all(nftQueries);
    
    // Flatten results
    return results.concat(...nftResults);
  } catch (error) {
    console.error('Error in fetchNFTs:', error);
    return [];
  }
}

// Main function to fetch all wallet assets (native + ERC-20 tokens)
// WITH INSTANT CACHING - Returns cached data immediately for repeat queries
export async function fetchMultiChainWalletAssets(
  address: string,
  chains: ChainId[]
): Promise<MultiChainToken[]> {
  const results: MultiChainToken[] = [];

  if (!address || address.trim().length === 0) {
    console.error('Invalid address provided:', address);
    return [];
  }

  if (!chains || chains.length === 0) {
    console.error('No chains provided');
    return [];
  }

  // Check cache first for INSTANT results
  const cached = getCachedData(address, chains);
  if (cached) {
    console.log(`⚡ INSTANT: Returning cached tokens for ${address} (${cached.tokens.length} tokens)`);
    return cached.tokens;
  }

  try {
    // Query all chains in parallel (use allSettled so one failure doesn't block others)
    const chainQueries = chains.map(async (chain) => {
      try {
        const chainMeta = getChainMetadata(chain);
        const [nativeBalance, erc20Tokens] = await Promise.all([
          fetchNativeBalance(chain, address),
          chainMeta?.supportsTokens ? fetchERC20Tokens(chain, address) : Promise.resolve([]),
        ]);

        // Add native token
        const nativeToken: MultiChainToken = {
          chain,
          contractAddress: undefined,
          symbol: getNativeTokenSymbol(chain),
          name: getNativeTokenName(chain),
          balance: nativeBalance,
          decimals: 18,
          logoUrl: undefined,
        };

        // Only include native token if balance > 0 (except Ethereum for debugging)
        const tokens: MultiChainToken[] = [];
        if (nativeBalance > 0 || chain === 'ethereum') {
          tokens.push(nativeToken);
        }
        
        tokens.push(...erc20Tokens);
        
        return tokens;
      } catch (error) {
        console.error(`Error fetching assets for chain ${chain}:`, error);
        return [];
      }
    });

    // Use allSettled so one chain failure doesn't block others
    const chainResults = await Promise.allSettled(chainQueries);
    
    // Extract successful results
    const successfulResults: MultiChainToken[][] = [];
    chainResults.forEach((result, index) => {
      if (result.status === 'fulfilled') {
        successfulResults.push(result.value);
        results.push(...result.value);
      } else {
        console.error(`Chain ${chains[index]} query failed:`, result.reason);
      }
    });
    
    // Cache results for instant future queries (tokens only, NFTs cached separately)
    // We'll update the cache after NFTs are fetched too
    
    return results;
  } catch (error) {
    console.error('Error in fetchMultiChainWalletAssets:', error);
    return [];
  }
}

// Main function to fetch all NFTs
// WITH INSTANT CACHING - Returns cached data immediately for repeat queries
export async function fetchMultiChainWalletNFTs(
  address: string,
  chains: ChainId[]
): Promise<MultiChainNFT[]> {
  const results: MultiChainNFT[] = [];

  if (!address || address.trim().length === 0) {
    console.error('Invalid address provided for NFTs:', address);
    return [];
  }

  if (!chains || chains.length === 0) {
    console.error('No chains provided for NFTs');
    return [];
  }

  // Check cache first for INSTANT results
  const cached = getCachedData(address, chains);
  if (cached) {
    console.log(`⚡ INSTANT: Returning cached NFTs for ${address} (${cached.nfts.length} NFTs)`);
    return cached.nfts;
  }

  try {
    // Query all chains in parallel (use allSettled so one failure doesn't block others)
    const nftQueries = chains.map(async (chain) => {
      try {
        return await fetchNFTs(chain, address);
      } catch (error) {
        console.error(`Error fetching NFTs for chain ${chain}:`, error);
        return [];
      }
    });
    
    // Use allSettled so one chain failure doesn't block others
    const nftResults = await Promise.allSettled(nftQueries);
    
    // Extract successful results
    nftResults.forEach((result, index) => {
      if (result.status === 'fulfilled') {
        results.push(...result.value);
      } else {
        console.error(`Chain ${chains[index]} NFT query failed:`, result.reason);
      }
    });
    
    // NOTE: Caching is handled in the UnifiedWalletViewer component
    // which calls both fetchMultiChainWalletAssets and fetchMultiChainWalletNFTs
    // and then caches the combined results
    
    return results;
  } catch (error) {
    console.error('Error in fetchMultiChainWalletNFTs:', error);
    return [];
  }
}

// Helper functions
function getNativeTokenSymbol(chain: ChainId): string {
  const chainMeta = getChainMetadata(chain);
  return chainMeta?.nativeSymbol || 'ETH';
}

function getNativeTokenName(chain: ChainId): string {
  const chainMeta = getChainMetadata(chain);
  return chainMeta?.nativeName || 'Ethereum';
}

